// Dialog destrutivo de desativação de contato.
// DESIGN_SYSTEM §componentes/Modal + voz: "Desativar" (não "deletar"),
// histórico preservado, motivo obrigatório.

import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import { cn } from '@control-center/ui';

import { PillButton } from '../clients/PillButton';

interface DeactivateContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactName: string;
  isPending?: boolean;
  onConfirm: (reason: string) => void;
}

export function DeactivateContactDialog({
  open,
  onOpenChange,
  contactName,
  isPending,
  onConfirm,
}: DeactivateContactDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const reasonId = useId();
  const reasonErrorId = `${reasonId}-error`;

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  function handleConfirm(): void {
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setError('Informe o motivo da desativação.');
      return;
    }
    onConfirm(trimmed);
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          aria-describedby="deactivate-contact-desc"
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(460px,calc(100vw-32px))] rounded-[14px] border border-line bg-panel shadow-lg',
            'p-7',
            'focus:outline-none',
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
                Desativar contato
              </Dialog.Title>
              <p
                id="deactivate-contact-desc"
                className="mt-2 text-[13px] leading-[1.55] text-muted"
              >
                Desativar <span className="font-medium text-ink-2">{contactName}</span>. O contato
                sai da lista ativa mas o histórico fica preservado. Você pode reativar depois.
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <div className="mt-5 space-y-2">
            <label htmlFor={reasonId} className="block text-[12px] font-medium text-ink-2">
              Motivo da desativação <span className="text-bad">*</span>
            </label>
            <textarea
              id={reasonId}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError(null);
              }}
              rows={3}
              maxLength={500}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? reasonErrorId : undefined}
              placeholder="Ex.: Saiu da empresa em 05/2026"
              className={cn(
                'w-full resize-y rounded-[10px] border bg-panel-2 px-3 py-2 text-[13px] text-ink outline-none placeholder:text-muted-2 focus:border-line-2',
                error ? 'border-bad/70' : 'border-line',
              )}
            />
            {error && (
              <p id={reasonErrorId} className="text-[12px] text-red-300/80">
                {error}
              </p>
            )}
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <Dialog.Close asChild>
              <PillButton disabled={isPending}>Manter ativo</PillButton>
            </Dialog.Close>
            <PillButton danger onClick={handleConfirm} disabled={isPending}>
              {isPending ? 'Desativando…' : 'Desativar contato'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
